import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { StoreNames } from "src/app/constants/storage-names";

import { SessionService } from './storage/session.service';
import { ApiService } from './api.service';

import { BenefitCategory } from '../models/benefit-category';
import { SessionStatus } from '../constants/session';
import { BenefitSummaryEmailCriteria, BenefitSummaryItemList, BenefitSummaryList } from '../models/benefit-summary';
import { ToastService } from './toast.service';
import { ToastType } from '../constants/toast-type';
import { ApiHeaderType } from '../constants/api-header-type';
import { environment } from 'src/environments/environment';
import { EnvironmentType } from '../constants/environment-type';


const controllerRoute = '/mybenefits';
const membersRoute = '/members';

@Injectable()
export class BenefitService {
    public currentBenefits: BenefitSummaryList;
    public enrollmentRequests: BenefitSummaryList;

    public benefitCategoriesList: BenefitCategory[];
    public sessionStore: Observable<any>;

    constructor(
        private apiService: ApiService,
        private sessionService: SessionService,
        private toastService: ToastService,
    ) {
        this.sessionStore = this.sessionService.$SessionClear;
        this.sessionStore.subscribe((status: string) => {
            switch (status) {
                case SessionStatus.initalize:
                    let sessionState = this.sessionService.retrieve(StoreNames.benefitCategoriesList);
                    if (sessionState)
                        this.benefitCategoriesList = sessionState;
                    break;

                case SessionStatus.clearMember:
                    this.benefitCategoriesList =
                        this.currentBenefits =
                        this.enrollmentRequests = null;
                    this.sessionService.remove(StoreNames.benefitCategoriesList);
                    break;

                case SessionStatus.clearSession:
                    this.benefitCategoriesList =
                        this.currentBenefits =
                        this.enrollmentRequests = null;
                    this.sessionService.remove(StoreNames.benefitCategoriesList);
                    break;
            }
        });
    }

    public resetMyBenefitsList(): void {
        this.currentBenefits = null;
        this.enrollmentRequests = null;
    }

    public getBenefitCategoreis(): Observable<BenefitCategory[]> {
        if (this.benefitCategoriesList)
            return of(this.benefitCategoriesList);

        return this.apiService.get(`${controllerRoute}/benefit-categories`).pipe(
            map((response: BenefitCategory[]) => {
                this.benefitCategoriesList = response;
                this.sessionService.store(StoreNames.benefitCategoriesList, this.benefitCategoriesList);
                return this.benefitCategoriesList;
            }),
        );
    }

    public getBenefits(): Observable<BenefitSummaryList> {
        if (this.currentBenefits)
            return of(this.currentBenefits);

        return this.apiService.get(`${controllerRoute}`).pipe(
            map((response: BenefitSummaryList) => {
                this.currentBenefits = response;
                return this.currentBenefits;
            }),
        );
    }

    public getEnrollmentRequests(): Observable<BenefitSummaryList> {
        if (this.enrollmentRequests)
            return of(this.enrollmentRequests);

        return this.apiService.get(`${controllerRoute}/enrollment-requests`).pipe(
            map((response: BenefitSummaryList) => {
                this.enrollmentRequests = response;
                return this.enrollmentRequests;
            }),
        );
    }

    public getBenefitSummaries(): Observable<BenefitSummaryItemList> {
        return this.apiService.get(`${controllerRoute}/benefit-summaries`).pipe(
            map((response: BenefitSummaryItemList) => {
                return response;
            })
        );
    }

    public getBenefitSummary(id: number): Observable<Blob> {
        return this.apiService.get(`${membersRoute}/benefit-summary/${id}`, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            })
        );
    }

    /**
     * API only sends a 204
     * @param BenefitSummaryEmailCriteria
     * @returns
     */
    public sendBenefitSummaryToEmail(payload: BenefitSummaryEmailCriteria): Observable<any> {
        return this.apiService.post(`${controllerRoute}/benefit-summary-email`, payload).pipe(
            map(() => {
                switch (environment.type){
                    case EnvironmentType.Prod:
                        this.toastService.buildToastMessage(ToastType.success, 'Email Sent', 'Please check your email shortly.');
                        break;

                    default:
                        this.toastService.buildToastMessage(ToastType.warning, 'Email Notice', 'Email request saved but will not be sent from this environment.');
                        break;
                }
            })
        );
    }
}